import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BsChevronRight, BsLockFill, BsMap, BsShieldFill, BsStars, BsTrophyFill } from 'react-icons/bs';
import { buildUrlWithParams } from '../../utils.js';
import CardCarousel from '../components/CardCarousel';
import MediaFrame from '../components/MediaFrame';
import useStablePanelHeight from '../hooks/useStablePanelHeight';
import { backgroundOverlay, backgroundStyle, backgroundValue, cleanHtml } from '../utils/presentation';
import { imageUrl } from '../utils/setModel';
import './SoloAdventureModule.css';

const FORMAT_COPY = {
  classic: { format: 'Classic Adventure', chapter: 'Wing', chapters: 'Wings', encounters: 'Bosses' },
  missions: { format: 'Solo Missions', chapter: 'Mission', chapters: 'Missions', encounters: 'Encounters' },
  run: { format: 'Dungeon Run', chapter: 'Zone', chapters: 'Zones', encounters: 'Encounters' },
  puzzle: { format: 'Puzzle Collection', chapter: 'Lab', chapters: 'Labs', encounters: 'Puzzles' },
  story: { format: 'Story Campaign', chapter: 'Chapter', chapters: 'Chapters', encounters: 'Encounters' },
  custom: { format: 'Custom Adventure', chapter: 'Chapter', chapters: 'Chapters', encounters: 'Encounters' },
};

const REWARD_LABELS = {
  treasure: 'Active Treasure', passive: 'Passive Treasure', collectible: 'Collectible Reward',
  completion: 'Completion Reward', custom: 'Special Reward',
};

const cardImage = (card) => `https://www.hearthcards.net/cards/${card.cardid}.webp`;
const cardDestination = (card) => buildUrlWithParams({
  ...Object.fromEntries(new URLSearchParams(window.location.search)),
  selectedCard: card.id,
});

function CardLink({ card, className = '' }) {
  return <Link className={className} to={cardDestination(card)} preventScrollReset><img src={cardImage(card)} alt={card.cardname || 'Adventure card'} /></Link>;
}

function Artwork({ value, frame, alt, onImageClick, className = '' }) {
  const url = imageUrl(value);
  if (!url) return null;
  return (
    <div className={className}>
      <MediaFrame type={frame}>
        <button type="button" className="set-adventure-art-button" onClick={() => onImageClick?.(url)}>
          <img src={url} alt={alt} />
        </button>
      </MediaFrame>
    </div>
  );
}

function HeroFeature({ feature, onImageClick }) {
  const item = feature.data || {};
  const power = Array.isArray(item.heropower) ? item.heropower[0] : null;
  const deck = Array.isArray(item.deckcards) ? item.deckcards.slice(0, 4) : [];
  return (
    <article className="set-adventure-hero-card">
      <Artwork value={item.image} frame={item.mediaFrame} alt={item.title || 'Playable hero'} onImageClick={onImageClick} className="set-adventure-hero-art" />
      <div className="set-adventure-hero-copy">
        {item.subtitle && <span>{item.subtitle}</span>}
        {item.title && <h4>{item.title}</h4>}
        {item.description && <div dangerouslySetInnerHTML={{ __html: cleanHtml(item.description) }} />}
        {item.unlock && <p className="set-adventure-unlock"><BsLockFill /> {item.unlock}</p>}
      </div>
      {(power || deck.length > 0) && <div className="set-adventure-hero-loadout">
        {power && <div><small>Hero Power</small><CardLink card={power} /></div>}
        {deck.length > 0 && <div className="set-adventure-deck-preview"><small>Starting Deck</small><div>{deck.map((card, index) => <CardLink key={`${card.id || card.cardid}-${index}`} card={card} />)}</div></div>}
      </div>}
    </article>
  );
}

function RewardFeature({ feature, onImageClick }) {
  const item = feature.data || {};
  const cards = Array.isArray(item.cards) ? item.cards : [];
  return (
    <article className="set-adventure-reward-card">
      <Artwork value={item.image} frame={item.mediaFrame} alt={item.title || 'Adventure reward'} onImageClick={onImageClick} className="set-adventure-reward-art" />
      <div>
        <span><BsStars /> {REWARD_LABELS[item.category] || REWARD_LABELS.custom}</span>
        {item.title && <h4>{item.title}</h4>}
        {item.description && <div dangerouslySetInnerHTML={{ __html: cleanHtml(item.description) }} />}
        {item.unlock && <p className="set-adventure-unlock"><BsLockFill /> {item.unlock}</p>}
      </div>
      {cards.length > 0 && <div className="set-adventure-reward-cards">{cards.slice(0, 3).map((card, index) => <CardLink key={`${card.id || card.cardid}-${index}`} card={card} />)}</div>}
    </article>
  );
}

export default function SoloAdventureModule({ module, onImageClick }) {
  const data = module.data || {};
  const chapters = useMemo(() => (module.features || []).filter((feature) => feature.type === 'chapter'), [module.features]);
  const heroes = useMemo(() => (module.features || []).filter((feature) => feature.type === 'hero'), [module.features]);
  const rewards = useMemo(() => (module.features || []).filter((feature) => feature.type === 'reward'), [module.features]);
  const [activeChapter, setActiveChapter] = useState(0);
  useEffect(() => setActiveChapter((index) => Math.min(index, Math.max(0, chapters.length - 1))), [chapters.length]);
  const stableChapter = useStablePanelHeight(chapters.length);
  const chapter = chapters[activeChapter]?.data || {};
  const format = FORMAT_COPY[data.format] || FORMAT_COPY.custom;
  const chapterLabel = data.chapterLabel?.trim() || format.chapters;
  const chapterUnit = data.chapterLabel?.trim() || format.chapter;
  const encounterLabel = data.encounterLabel?.trim() || format.encounters;
  const chapterCards = Array.isArray(chapter.cards) ? chapter.cards : [];
  const keyArt = imageUrl(data.keyArt);
  const style = {
    ...backgroundStyle(backgroundValue(data), { size: data.backgroundSize || 'cover', position: data.backgroundPosition || 'center', tileSize: data.backgroundTileSize }),
    backgroundColor: data.backgroundColor || '#171323',
    color: data.textColor || '#ffffff',
    '--section-overlay': backgroundOverlay(data, 35),
    '--adventure-accent': data.accentColor || '#f4c542',
  };

  return (
    <section className={`set-presentation-section set-solo-adventure set-adventure-layout-${data.layout || 'map'}`} style={style}>
      <div className="set-presentation-shade" />
      <div className="container set-adventure-inner">
        <header className={`set-adventure-header ${keyArt ? 'set-has-key-art' : ''}`}>
          <div className="set-adventure-header-copy">
            <span className="set-presentation-eyebrow">{data.eyebrow || format.format}</span>
            {data.title && <h2>{data.title}</h2>}
            {data.intro && <div className="set-adventure-intro" dangerouslySetInnerHTML={{ __html: cleanHtml(data.intro) }} />}
            <div className="set-adventure-summary">
              {chapters.length > 0 && <span><BsMap /> {chapters.length} {chapters.length === 1 ? chapterUnit : chapterLabel}</span>}
              {heroes.length > 0 && <span><BsShieldFill /> {heroes.length} {heroes.length === 1 ? 'Hero' : 'Heroes'}</span>}
              {rewards.length > 0 && <span><BsTrophyFill /> {rewards.length} {rewards.length === 1 ? 'Reward' : 'Rewards'}</span>}
            </div>
          </div>
          {keyArt && <Artwork value={data.keyArt} frame={data.mediaFrame} alt={data.title || 'Adventure key artwork'} onImageClick={onImageClick} className="set-adventure-key-art" />}
        </header>

        {chapters.length > 0 && <section className="set-adventure-journey" aria-labelledby={`adventure-journey-${module.id}`}>
          <div className="set-adventure-journey-heading"><span>Choose your path</span><h3 id={`adventure-journey-${module.id}`}>{chapterLabel}</h3></div>
          <div className="set-adventure-journey-layout">
            <div className="set-adventure-chapter-nav" role="tablist" aria-label={chapterLabel}>
              {chapters.map((feature, index) => {
                const item = feature.data || {};
                const active = index === activeChapter;
                return <button key={feature.id || index} type="button" role="tab" aria-selected={active} className={active ? 'set-is-active' : ''} onClick={() => setActiveChapter(index)}><i>{index + 1}</i><span><small>{item.label || `${chapterUnit} ${index + 1}`}</small><strong>{item.title || 'Untitled chapter'}</strong></span><BsChevronRight /></button>;
              })}
            </div>
            <article className="set-adventure-chapter-panel" ref={stableChapter.ref} style={stableChapter.minHeight ? { minHeight: `${stableChapter.minHeight}px` } : undefined} role="tabpanel">
              <div className={`set-adventure-chapter-main ${imageUrl(chapter.image) ? 'set-has-art' : ''}`}>
                <Artwork value={chapter.image} frame={chapter.mediaFrame} alt={chapter.title || 'Chapter artwork'} onImageClick={onImageClick} className="set-adventure-chapter-art" />
                <div className="set-adventure-chapter-copy">
                  <span>{chapter.label || `${chapterUnit} ${activeChapter + 1}`}</span>
                  {chapter.title && <h3>{chapter.title}</h3>}
                  {chapter.description && <div dangerouslySetInnerHTML={{ __html: cleanHtml(chapter.description) }} />}
                  {(chapter.ruleTitle || chapter.rule) && <aside className="set-adventure-rule"><small>Special Rule</small>{chapter.ruleTitle && <strong>{chapter.ruleTitle}</strong>}{chapter.rule && <p>{chapter.rule}</p>}</aside>}
                  {chapter.reward && <p className="set-adventure-completion"><BsTrophyFill /> <span><small>Completion Reward</small>{chapter.reward}</span></p>}
                  {chapter.heroic && <p className="set-adventure-heroic"><BsShieldFill /> <span><small>Heroic Challenge</small>{chapter.heroic}</span></p>}
                </div>
              </div>
              {chapterCards.length > 0 && <div className="set-adventure-encounters"><h4>{encounterLabel}</h4><CardCarousel variableWidth cardWidth={210} mobileCenter desktopArrowThreshold={5}>{chapterCards.map((card, index) => <CardLink key={`${card.id || card.cardid}-${index}`} card={card} />)}</CardCarousel></div>}
            </article>
          </div>
        </section>}

        {heroes.length > 0 && <section className="set-adventure-feature-section"><div className="set-adventure-section-heading"><span>Choose your champion</span><h3>Playable Heroes</h3></div><div className="set-adventure-hero-grid">{heroes.map((feature, index) => <HeroFeature key={feature.id || index} feature={feature} onImageClick={onImageClick} />)}</div></section>}
        {rewards.length > 0 && <section className="set-adventure-feature-section"><div className="set-adventure-section-heading"><span>Power for the journey</span><h3>Treasures &amp; Rewards</h3></div><div className="set-adventure-reward-grid">{rewards.map((feature, index) => <RewardFeature key={feature.id || index} feature={feature} onImageClick={onImageClick} />)}</div></section>}
      </div>
    </section>
  );
}
